import { Component, ChangeDetectionStrategy, effect } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { TagModule } from 'primeng/tag';
import { FormStatus } from '@fitness-force/survey-form-engine';

@Component({
  selector: 'form-status-tag',
  standalone: true,
  imports: [TagModule],
  template: `
    @if (status !== null) {
      <p-tag 
        [value]="getStatusLabel()" 
        [severity]="getStatusSeverity()"
        [attr.aria-label]="'status-' + status"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormStatusTagComponent {
  columnData: Observable<any> = new BehaviorSubject(null);
  additionData: Observable<any> = new BehaviorSubject(null);
  status: FormStatus | null = null;

  constructor() {
    effect(() => {
      this.columnData.subscribe((data: any) => {
        if (data != null && data.status !== undefined && data.status !== null) {
          this.status = this.normalizeStatus(data.status);
        }
      });
    });
  }

  private normalizeStatus(value: unknown): FormStatus | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number' && !Number.isNaN(value)) {
      return this.isFormStatus(value) ? value : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const numeric = Number(trimmed);
      if (!Number.isNaN(numeric) && this.isFormStatus(numeric)) {
        return numeric;
      }

      const enumKey = trimmed.toUpperCase() as keyof typeof FormStatus;
      if (enumKey in FormStatus && typeof FormStatus[enumKey] === 'number') {
        return FormStatus[enumKey];
      }
    }

    return null;
  }

  private isFormStatus(value: number): value is FormStatus {
    return Object.values(FormStatus).filter((v) => typeof v === 'number').includes(value);
  }

  getStatusLabel(): string {
    switch (this.status) {
      case FormStatus.PENDING:
        return 'Pending';
      case FormStatus.DRAFT:
        return 'Draft';
      case FormStatus.COMPLETED:
        return 'Completed';
      case FormStatus.EXPIRED:
        return 'Expired';
      default:
        return String(this.status || '');
    }
  }

  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (this.status) {
      case FormStatus.COMPLETED:
        return 'success';
      case FormStatus.DRAFT:
        return 'info';
      case FormStatus.PENDING:
        return 'warn';
      case FormStatus.EXPIRED:
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
